import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Paper, TextField, Button, CircularProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ApiGet, ApiPost, ApiPut, ApiDelete } from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import { DataTable } from "../components/DataTable";
import { usePermissions } from "../hooks/usePermissions";
import { PageHeader } from "../components/PageHeader";
import { useImageCrop, useContentLanguage } from "../hooks";
import { sanitizeInput } from "../utils/sanitize";
import { buildCompanyColumns } from "./company/companyColumns";
import { LanguageTabs } from "../components/LanguageTabs";
import { CompanyFormDialog } from "./company/CompanyFormDialog";
import { CompanyDraggableList } from "./company/CompanyDraggableList";

export default function CompanyPage() {
  const { canCreate, canEdit, canDelete } = usePermissions("/company");
  const [companyData, setCompanyData] = useState<any>({});
  const [addCompany, setAddCompany] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllCompany, setGetAllCompany] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [companyId, setCompanyId] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>("manual");
  const [removeCompanyLogo, setRemoveCompanyLogo] = useState(false);
  const [removeCompanyLogoGer, setRemoveCompanyLogoGer] = useState(false);

  const [lang, setLang] = useContentLanguage();

  const imageCropEn = useImageCrop();
  const imageCropGer = useImageCrop();

  useEffect(() => {
    getAllCompanyData();
  }, [page, countPerPage, search, sortOrder]);

  const addCompanyModelClose = () => {
    setAddCompany(false);
    setIsEditApi(false);
    setErrors({});
    setCompanyData({});
    imageCropEn.reset();
    imageCropGer.reset();
    setRemoveCompanyLogo(false);
    setRemoveCompanyLogoGer(false);
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setCompanyData({ ...companyData, [name]: sanitizeInput(value) });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errors: any = {};
    if (!companyData?.companyName?.trim()) {
      formIsValid = false;
      errors["companyName"] = "*Bitte geben Sie den englischen Firmennamen ein";
    }
    if (!companyData?.companyName_ger?.trim()) {
      formIsValid = false;
      errors["companyName_ger"] = "*Bitte geben Sie den deutschen Firmennamen ein";
    }
    setErrors(errors);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllCompanyData = async () => {
    setLoaderForGetAll(true);
    const params = sortOrder === "manual"
      ? `isActive=true&limit=1000${search ? `&letter=${search}` : ""}`
      : `isActive=true&page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}&sort=${sortOrder}`;
    await ApiGet(`company-profile/find?${params}`)
      .then((res: any) => {
        setGetAllCompany(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const addCompanyData = async () => {
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = new FormData();
        formData.append("companyName", companyData?.companyName);
        formData.append("companyName_ger", companyData?.companyName_ger);
        if (companyData?.email) formData.append("email", companyData?.email);
        if (companyData?.telephoneNo) formData.append("telephoneNo", companyData?.telephoneNo);
        if (companyData?.website) formData.append("website", companyData?.website);
        if (companyData?.companyDetails) formData.append("companyDetails", companyData?.companyDetails);
        if (companyData?.companyDetails_ger) formData.append("companyDetails_ger", companyData?.companyDetails_ger);
        if (companyData?.address) formData.append("address", companyData?.address);
        if (companyData?.address_ger) formData.append("address_ger", companyData?.address_ger);

        const croppedEn = await imageCropEn.getCroppedFile();
        if (croppedEn) formData.append("companyLogo", croppedEn);
        const croppedGer = await imageCropGer.getCroppedFile();
        if (croppedGer) formData.append("companyLogo_ger", croppedGer);

        await ApiPost("company-profile/create", formData);
        toast.success("Unternehmensdaten fügen erfolgreich hinzu");
        setAddCompany(false);
        setIsEditApi(false);
        getAllCompanyData();
        setCompanyData({});
        imageCropEn.reset();
        imageCropGer.reset();
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      } finally {
        setLoader(false);
      }
    }
  };

  const editCompanyData = async () => {
    if (formValidation()) {
      setLoader(true);
      try {
        const formData = new FormData();
        formData.append("companyName", companyData?.companyName);
        formData.append("companyName_ger", companyData?.companyName_ger);
        formData.append("email", companyData?.email ?? "");
        formData.append("telephoneNo", companyData?.telephoneNo ?? "");
        formData.append("website", companyData?.website ?? "");
        formData.append("companyDetails", companyData?.companyDetails ?? "");
        formData.append("companyDetails_ger", companyData?.companyDetails_ger ?? "");
        formData.append("address", companyData?.address ?? "");
        formData.append("address_ger", companyData?.address_ger ?? "");

        const croppedEn = await imageCropEn.getCroppedFile();
        if (croppedEn) formData.append("companyLogo", croppedEn);
        const croppedGer = await imageCropGer.getCroppedFile();
        if (croppedGer) formData.append("companyLogo_ger", croppedGer);
        if (removeCompanyLogo) formData.append("removeCompanyLogo", "true");
        if (removeCompanyLogoGer) formData.append("removeCompanyLogo_ger", "true");

        await ApiPut(`company-profile/update?id=${companyId}`, formData);
        toast.success("Unternehmensdaten aktualisieren erfolgreich");
        getAllCompanyData();
        setAddCompany(false);
        setIsEditApi(false);
        setCompanyData({});
        imageCropEn.reset();
        imageCropGer.reset();
        setRemoveCompanyLogo(false);
        setRemoveCompanyLogoGer(false);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      } finally {
        setLoader(false);
      }
    }
  };

  const deleteCompanyData = async () => {
    const formData = new FormData();
    formData.append("isActive", "false");
    await ApiDelete(`company-profile/delete?id=${companyId}`, formData)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Unternehmensdaten erfolgreich löschen");
          setShow(false);
          setPage(1);
          getAllCompanyData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const companyColumns = useMemo(() => buildCompanyColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setAddCompany(true);
      setCompanyId(row?._id);
      setCompanyData({
        companyName: row?.companyName,
        companyName_ger: row?.companyName_ger,
        email: row?.email,
        telephoneNo: row?.telephoneNo,
        website: row?.website,
        companyDetails: row?.companyDetails,
        companyDetails_ger: row?.companyDetails_ger,
        address: row?.address,
        address_ger: row?.address_ger,
        companyLogo: row?.companyLogo?.[0],
        companyLogo_ger: row?.companyLogo_ger?.[0],
      });
    },
    onDelete: (row: any) => {
      setCompanyId(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Unternehmensprofile"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setCompanyData({});
            setErrors({});
            imageCropEn.reset();
            imageCropGer.reset();
            setAddCompany(true);
          } : undefined}
        >
          <TextField
            select
            size="small"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 180 }}
          >
            <option value="manual">Manuelle Sortierung</option>
            <option value="alphabetical">Alphabetisch</option>
          </TextField>
        </PageHeader>
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : sortOrder === "manual" ? (
          <CompanyDraggableList
            rows={getAllCompany || []}
            lang={lang}
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={(row: any) => {
              setIsEditApi(true);
              setAddCompany(true);
              setCompanyId(row?._id);
              setCompanyData({
                companyName: row?.companyName,
                companyName_ger: row?.companyName_ger,
                email: row?.email,
                telephoneNo: row?.telephoneNo,
                website: row?.website,
                companyDetails: row?.companyDetails,
                companyDetails_ger: row?.companyDetails_ger,
                address: row?.address,
                address_ger: row?.address_ger,
                companyLogo: row?.companyLogo?.[0],
                companyLogo_ger: row?.companyLogo_ger?.[0],
              });
            }}
            onDelete={(row: any) => { setCompanyId(row?._id); setShow(true); }}
            onReordered={getAllCompanyData}
          />
        ) : (
          <DataTable
            rows={getAllCompany || []}
            columns={companyColumns}
            sx={{ tableLayout: "fixed" }}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Löschen bestätigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diese Unternehmensdaten löschen möchten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Nein
          </Button>
          <Button onClick={() => deleteCompanyData()} color="error" variant="contained">
            Ja
          </Button>
        </DialogActions>
      </Dialog>

      <CompanyFormDialog
        open={addCompany}
        isEditApi={isEditApi}
        companyData={companyData}
        errors={errors}
        loader={loader}
        imageCropEn={imageCropEn}
        imageCropGer={imageCropGer}
        onClose={addCompanyModelClose}
        onChange={onhandleChange}
        onSubmit={() => { isEditApi ? editCompanyData() : addCompanyData(); }}
        onRemoveCompanyLogo={() => {
          setCompanyData({ ...companyData, companyLogo: "" });
          setRemoveCompanyLogo(true);
        }}
        onRemoveCompanyLogoGer={() => {
          setCompanyData({ ...companyData, companyLogo_ger: "" });
          setRemoveCompanyLogoGer(true);
        }}
      />
    </>
  );
}
